import React, { useState } from 'react';
import { X, User, Radio, ClipboardList } from 'lucide-react';
import { motion } from 'framer-motion';

import OfficersTab    from './tabs/OfficersTab';
import LivePatrolTab  from './tabs/LivePatrolTab';
import ActivityLogsTab from './tabs/ActivityLogsTab';

const SHIFT_TABS = [
  { key: 'officers', label: 'Officers',      Icon: User          },
  { key: 'live',     label: 'Live Patrol',   Icon: Radio         },
  { key: 'activity', label: 'Activity Logs', Icon: ClipboardList },
];

/**
 * ShiftDetailPanel — right-pane in the split-pane work order detail.
 *
 * Owns which shift-level sub-tab is active (Officers / Live Patrol / Activity Logs).
 * ALL data arrives via props from CustomerJobDetail — this component does zero fetching.
 *
 * Props:
 *   shift             — shiftDetail.shift
 *   job               — shiftDetail.job  (needed for jobId in rate actions)
 *   offers[]          — shiftDetail.offers
 *   activityLogs[]    — shiftDetail.activityLogs
 *   filledCount       — shiftDetail.filledCount
 *   onClose()         — clear shift from URL
 *   onRefresh()       — re-fetch shiftDetail in CustomerJobDetail
 *   customerCompanyId
 */
const ShiftDetailPanel = ({
  shift,
  job,
  offers = [],
  activityLogs = [],
  filledCount = 0,
  onClose,
  onRefresh,
  customerCompanyId,
}) => {
  const [activeTab, setActiveTab] = useState('officers');

  if (!shift) return null;

  const jobId = job?._id?.toString();

  const coverageBadge =
    filledCount >= (shift.staffsRequired || 0)
      ? 'bg-emerald-100 text-emerald-700'
      : filledCount > 0
        ? 'bg-amber-100 text-amber-700'
        : 'bg-red-100 text-red-700';

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* ── Panel header ─────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between border-b border-border px-5 py-4 flex-shrink-0">
        <div>
          <p className="text-sm font-semibold text-foreground">
            {new Date(shift.date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
          </p>
          <p className="text-xs text-muted-foreground">
            {new Date(shift.startTime).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}
            {' – '}
            {new Date(shift.endTime).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${coverageBadge}`}>
            {filledCount}/{shift.staffsRequired || 0} filled
          </span>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted transition-colors"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* ── Shift-level sub-tab bar ───────────────────────────────────────── */}
      <div className="flex gap-1 border-b border-border px-4 pt-2 flex-shrink-0">
        {SHIFT_TABS.map(({ key, label, Icon }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`relative flex items-center gap-1.5 px-3 py-2 text-xs font-medium transition-colors rounded-t-md ${
              activeTab === key ? 'text-[#CC457D]' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon size={12} /> {label}
            {activeTab === key && (
              <motion.div
                layoutId="shift-tab-indicator"
                className="absolute bottom-0 left-0 right-0 h-0.5 rounded-t bg-[#CC457D]"
              />
            )}
          </button>
        ))}
      </div>

      {/* ── Tab content ──────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto p-5">
        {activeTab === 'officers' && (
          <OfficersTab
            offers={offers}
            filledCount={filledCount}
            customerCompanyId={customerCompanyId}
            jobId={jobId}
            onRateAction={onRefresh}
          />
        )}

        {activeTab === 'live' && (
          <LivePatrolTab shift={shift} job={job} />
        )}

        {activeTab === 'activity' && (
          <ActivityLogsTab activityLogs={activityLogs} />
        )}
      </div>
    </div>
  );
};

export default ShiftDetailPanel;
